import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Popover from '@radix-ui/react-popover';
import { clsx } from 'clsx';
import * as React from 'react';
import { RequiredLabel } from './RequiredLabel';
import { ArrowLeftIcon, CalendarIcon } from '../icons/index.js';
const DATE_VALUE_RE = /^\d{4}-\d{2}-\d{2}$/;
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
function parseDateValue(value) {
    if (!value || !DATE_VALUE_RE.test(value))
        return null;
    const parts = value.split('-');
    if (parts.length !== 3)
        return null;
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if ([year, month, day].some((part) => Number.isNaN(part))) {
        return null;
    }
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime()) ||
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day) {
        return null;
    }
    date.setHours(0, 0, 0, 0);
    return date;
}
function formatDateValue(date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}
function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}
function addMonths(date, amount) {
    return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}
function startOfWeek(date) {
    const copy = new Date(date);
    copy.setDate(copy.getDate() - copy.getDay());
    copy.setHours(0, 0, 0, 0);
    return copy;
}
function isSameDay(left, right) {
    return Boolean(left &&
        right &&
        left.getFullYear() === right.getFullYear() &&
        left.getMonth() === right.getMonth() &&
        left.getDate() === right.getDate());
}
function buildCalendarDays(monthDate) {
    const firstDay = startOfWeek(startOfMonth(monthDate));
    return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(firstDay);
        date.setDate(firstDay.getDate() + index);
        date.setHours(0, 0, 0, 0);
        return date;
    });
}
function isBeforeMin(date, minDate) {
    return Boolean(minDate && date.getTime() < minDate.getTime());
}
function isAfterMax(date, maxDate) {
    return Boolean(maxDate && date.getTime() > maxDate.getTime());
}
/**
 * TextField component for single-line text input
 */
export const TextField = React.forwardRef(({ className, label, required = false, error, helperText, suppressErrorMessage = false, showRequiredText = false, id, size, type, disabled, value, defaultValue, onChange, placeholder, min, max, ...props }, ref) => {
    const inputId = id || React.useId();
    const hasError = Boolean(error);
    const describedBy = [hasError ? `${inputId}-error` : null, helperText ? `${inputId}-helper` : null]
        .filter(Boolean)
        .join(' ') || undefined;
    const inputRef = React.useRef(null);
    const controlRef = React.useRef(null);
    // Set around programmatic refocus (day select / Clear) so the focus handler
    // does not immediately reopen the popover we just closed.
    const suppressOpenOnFocusRef = React.useRef(false);
    const isDateInput = type === 'date';
    const controlledDateValue = typeof value === 'string' ? value : undefined;
    const [uncontrolledDateValue, setUncontrolledDateValue] = React.useState(() => typeof defaultValue === 'string' ? defaultValue : '');
    const [datePopoverOpen, setDatePopoverOpen] = React.useState(false);
    const today = React.useMemo(() => {
        const current = new Date();
        current.setHours(0, 0, 0, 0);
        return current;
    }, []);
    const dateValue = controlledDateValue ?? uncontrolledDateValue;
    const selectedDate = React.useMemo(() => parseDateValue(dateValue), [dateValue]);
    const minDate = React.useMemo(() => (typeof min === 'string' ? parseDateValue(min) : null), [min]);
    const maxDate = React.useMemo(() => (typeof max === 'string' ? parseDateValue(max) : null), [max]);
    const [visibleMonth, setVisibleMonth] = React.useState(() => startOfMonth(selectedDate ?? minDate ?? today));
    const calendarDays = React.useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth]);
    React.useImperativeHandle(ref, () => inputRef.current, []);
    const handleInputRef = React.useCallback((node) => {
        inputRef.current = node;
    }, []);
    const emitDateChange = React.useCallback((nextValue) => {
        const node = inputRef.current;
        if (!node)
            return;
        const valueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        valueSetter?.call(node, nextValue);
        node.dispatchEvent(new Event('input', { bubbles: true }));
        node.dispatchEvent(new Event('change', { bubbles: true }));
    }, []);
    const handleDateInputChange = React.useCallback((event) => {
        if (controlledDateValue === undefined) {
            setUncontrolledDateValue(event.target.value);
        }
        onChange?.(event);
    }, [controlledDateValue, onChange]);
    const handleDateSelect = React.useCallback((date) => {
        emitDateChange(formatDateValue(date));
        setVisibleMonth(startOfMonth(date));
        setDatePopoverOpen(false);
        suppressOpenOnFocusRef.current = true;
        inputRef.current?.focus();
        suppressOpenOnFocusRef.current = false;
    }, [emitDateChange]);
    const handleDateClear = React.useCallback(() => {
        emitDateChange('');
        setDatePopoverOpen(false);
        setVisibleMonth(startOfMonth(today));
        suppressOpenOnFocusRef.current = true;
        inputRef.current?.focus();
        suppressOpenOnFocusRef.current = false;
    }, [emitDateChange, today]);
    const handleDatePopoverOpenChange = React.useCallback((nextOpen) => {
        setDatePopoverOpen(nextOpen);
        if (nextOpen) {
            setVisibleMonth(startOfMonth(selectedDate ?? minDate ?? today));
        }
    }, [minDate, selectedDate, today]);
    const openDatePopoverFromInput = React.useCallback(() => {
        if (disabled || props.readOnly || datePopoverOpen)
            return;
        handleDatePopoverOpenChange(true);
    }, [disabled, props.readOnly, datePopoverOpen, handleDatePopoverOpenChange]);
    const handleDateInputClick = React.useCallback((event) => {
        props.onClick?.(event);
        openDatePopoverFromInput();
    }, [props.onClick, openDatePopoverFromInput]);
    const handleDateInputFocus = React.useCallback((event) => {
        props.onFocus?.(event);
        if (suppressOpenOnFocusRef.current)
            return;
        openDatePopoverFromInput();
    }, [props.onFocus, openDatePopoverFromInput]);
    const inputNode = (_jsx("input", { id: inputId, ref: handleInputRef, type: isDateInput ? 'text' : type, disabled: disabled, className: clsx('text-field__input', size === 'comfortable' && 'text-field__input--comfortable', hasError && 'text-field__input--error', isDateInput && 'text-field__input--date'), value: isDateInput ? dateValue : value, defaultValue: isDateInput ? undefined : defaultValue, placeholder: isDateInput ? (placeholder ?? 'YYYY-MM-DD') : placeholder, inputMode: isDateInput ? 'numeric' : props.inputMode, autoComplete: isDateInput ? 'off' : props.autoComplete, onChange: isDateInput ? handleDateInputChange : onChange, "aria-invalid": hasError, "aria-describedby": describedBy, ...props, onClick: isDateInput ? handleDateInputClick : props.onClick, onFocus: isDateInput ? handleDateInputFocus : props.onFocus }));
    return (_jsxs("div", { className: clsx('text-field', className), children: [(label || helperText) && (_jsxs("div", { className: "text-field__header", children: [label && (_jsx("label", { htmlFor: inputId, className: "text-field__label", children: _jsx(RequiredLabel, { label: label, required: required, showRequiredText: showRequiredText || hasError }) })), helperText && (_jsx("span", { id: `${inputId}-helper`, className: "text-field__helper", children: helperText }))] })), isDateInput ? (_jsxs(Popover.Root, { open: datePopoverOpen, onOpenChange: handleDatePopoverOpenChange, children: [_jsx(Popover.Anchor, { asChild: true, children: _jsxs("div", { className: "text-field__control", ref: controlRef, children: [inputNode, _jsx(Popover.Trigger, { asChild: true, children: _jsx("button", { type: "button", className: "text-field__date-trigger", "aria-label": "Open calendar", disabled: disabled || props.readOnly, children: _jsx(CalendarIcon, { className: "text-field__date-trigger-icon" }) }) })] }) }), _jsx(Popover.Portal, { children: _jsx(Popover.Content, { className: "text-field__date-popover", align: "end", sideOffset: 6, onOpenAutoFocus: (event) => event.preventDefault(), onInteractOutside: (event) => {
                                // A click back on the input (inside the anchor) must not
                                // dismiss the popover - it would close and instantly reopen.
                                const target = event.target;
                                if (target && controlRef.current?.contains(target)) {
                                    event.preventDefault();
                                }
                            }, children: _jsxs("div", { className: "text-field__date-calendar", role: "dialog", "aria-label": "Choose date", children: [_jsxs("div", { className: "text-field__date-calendar-header", children: [_jsx("button", { type: "button", className: "text-field__date-nav", onClick: () => setVisibleMonth((current) => addMonths(current, -1)), "aria-label": "Previous month", children: _jsx(ArrowLeftIcon, { width: 14, height: 14 }) }), _jsx("div", { className: "text-field__date-month", children: monthFormatter.format(visibleMonth) }), _jsx("button", { type: "button", className: "text-field__date-nav text-field__date-nav--next", onClick: () => setVisibleMonth((current) => addMonths(current, 1)), "aria-label": "Next month", children: _jsx(ArrowLeftIcon, { width: 14, height: 14 }) })] }), _jsx("div", { className: "text-field__date-weekdays", "aria-hidden": "true", children: WEEKDAY_LABELS.map((weekday) => (_jsx("span", { className: "text-field__date-weekday", children: weekday }, weekday))) }), _jsx("div", { className: "text-field__date-grid", children: calendarDays.map((day) => {
                                            const isOutsideMonth = day.getMonth() !== visibleMonth.getMonth();
                                            const isDisabledDay = isBeforeMin(day, minDate) || isAfterMax(day, maxDate) || disabled;
                                            const isSelected = isSameDay(day, selectedDate);
                                            const isToday = isSameDay(day, today);
                                            return (_jsx("button", { type: "button", className: clsx('text-field__date-day', isOutsideMonth && 'text-field__date-day--outside', isSelected && 'text-field__date-day--selected', isToday && 'text-field__date-day--today'), onClick: () => handleDateSelect(day), disabled: isDisabledDay, "aria-pressed": isSelected, children: day.getDate() }, day.toISOString()));
                                        }) }), _jsxs("div", { className: "text-field__date-footer", children: [_jsx("button", { type: "button", className: "text-field__date-footer-btn", onClick: handleDateClear, disabled: disabled || props.readOnly || !dateValue, children: "Clear" }), _jsx("button", { type: "button", className: "text-field__date-footer-btn", onClick: () => handleDateSelect(today), disabled: disabled ||
                                                    props.readOnly ||
                                                    isBeforeMin(today, minDate) ||
                                                    isAfterMax(today, maxDate), children: "Today" })] })] }) }) })] })) : (inputNode), error && !suppressErrorMessage && (_jsx("p", { id: `${inputId}-error`, className: "text-field__error", children: error }))] }));
});
TextField.displayName = 'TextField';
//# sourceMappingURL=TextField.js.map